// AbsorbersPanel.jsx - who did the absorbing on one footprint Absorption cell.
// Opened by a still click / tap on an Absorption outline (FootprintChartCanvas,
// sol charts only). Server: /api/wallet/absorbers (saved swaps, no credits).
//   bid outline = sells were absorbed -> absorbers are the BUYERS in that bar
//   ask outline = buys were absorbed  -> absorbers are the SELLERS
// 'Later' = which absorbers traded this coin again in the next N hours, and
// when they first came back.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { fetchAbsorbers, openWalletMap } from '../utils/walletSources'

const GROUP_COLORS = ['#ffb84a', '#4a9eff', '#c77dff', '#00d4c8', '#ff6fb5', '#9be564']
const short = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : '')
const ctFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
const pct = (v) => `${Math.round((v || 0) * 100)}%`
const fmtMin = (m) => (m < 60 ? `${Math.round(m)}m` : `${(m / 60).toFixed(m < 600 ? 1 : 0)}h`)
const AFTER_OPTS = [2, 6, 12, 24]
const VERDICT = { single: 'one wallet', few: 'a few wallets', many: 'many wallets', none: 'no attributed trades' }

export default function AbsorbersPanel({ at, onClose }) {
  const [afterH, setAfterH] = useState(6)
  const [st, setSt] = useState({ data: null, err: null, loading: false })
  const [pos, setPos] = useState({ left: 8, top: 8 })
  const boxRef = useRef(null)

  useEffect(() => {
    if (!at) return
    let alive = true
    setSt({ data: null, err: null, loading: true })
    fetchAbsorbers({ ...at, after_h: afterH })
      .then(d => { if (alive) setSt({ data: d, err: null, loading: false }) })
      .catch(e => { if (alive) setSt({ data: null, err: e.message || 'failed', loading: false }) })
    return () => { alive = false }
  }, [at, afterH])

  useEffect(() => {
    if (!at) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) onClose() }
    window.addEventListener('keydown', onKey)
    // capture + next tick: the opening tap must not close it
    const id = setTimeout(() => window.addEventListener('pointerdown', onDown, true), 0)
    return () => { clearTimeout(id); window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', onDown, true) }
  }, [at, onClose])

  useLayoutEffect(() => {
    if (!at || !boxRef.current) return
    const r = boxRef.current.getBoundingClientRect()
    const vw = window.innerWidth, vh = window.innerHeight
    let left = at.x + 12
    if (left + r.width > vw - 8) left = at.x - 12 - r.width
    left = Math.max(8, Math.min(left, vw - r.width - 8))
    const top = Math.max(8, Math.min(at.y - 20, vh - r.height - 8))
    setPos(p => (p.left === left && p.top === top ? p : { left, top }))
  }, [at, st])

  if (!at) return null
  const d = st.data
  const ws = d?.wallets || []
  const openTimeline = () => {
    const addrs = ws.slice(0, 20).map(w => w.addr)
    if (!addrs.length) return
    window.dispatchEvent(new CustomEvent('solcheddar-open-wallet', {
      detail: { address: addrs[0], highlight: addrs, mint: at.mint, t0: at.t0, t1: at.t1, afterH },
    }))
    onClose()
  }
  const sideTxt = at.side === 'bid' ? 'Sells absorbed by buyers' : 'Buys absorbed by sellers'
  const sideCol = at.side === 'bid' ? '#00cc6a' : '#ff4d5e'

  return createPortal(
    <div ref={boxRef} className="absorbers-panel" onPointerDown={e => e.stopPropagation()}
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 10050, width: 'min(360px, calc(100vw - 16px))',
        maxHeight: 'min(70vh, 520px)', overflow: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', background: 'rgba(16,18,22,0.97)', border: '1px solid #2c3038',
        borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.5)', color: '#c8cfdc', fontSize: 11,
        fontFamily: '"JetBrains Mono", monospace', padding: '8px 10px' }}>
      <style>{'.absorbers-panel::-webkit-scrollbar{display:none}'}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <b style={{ color: sideCol }}>{sideTxt}</b>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={btn} title="Close">✕</button>
      </div>
      <div style={{ color: '#7d828c', marginTop: 2 }}>
        {ctFmt.format(at.t0)} CT{d ? ` · ${d.scope === 'level' ? 'at this level' : 'whole bar (few prints at the level)'}` : ''}
      </div>

      {st.loading && <div style={{ padding: '10px 0', color: '#7d828c' }}>loading wallets…</div>}
      {st.err && <div style={{ padding: '8px 0', color: '#ff7b7b' }}>{st.err}</div>}

      {d && (
        <>
          <div style={{ margin: '6px 0', lineHeight: 1.5 }}>
            <b style={{ color: d.verdict === 'single' ? '#ffb84a' : '#e6e9ef' }}>{VERDICT[d.verdict] || d.verdict}</b>
            {` · ${d.wallets_total} wallet${d.wallets_total === 1 ? '' : 's'} · ${d.total_sol} SOL vs ${d.aggressor_sol} SOL aggressor`}
            <br />
            {`top ${pct(d.top_share)} · top 3 ${pct(d.top3_share)}`}
            {d.groups ? <span style={{ color: '#ffb84a' }}>{` · ${d.groups} connected group${d.groups === 1 ? '' : 's'} = ${pct(d.connected_share)}`}</span> : ' · no links found'}
            {d.capped && <span style={{ color: '#ffb84a' }}> · partial (capped)</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '4px 0 6px', color: '#7d828c' }}>
            <span>Later: back on this coin within</span>
            {AFTER_OPTS.map(h => (
              <button key={h} onClick={() => setAfterH(h)} style={{ ...btn, ...(h === afterH ? btnOn : null) }}>{h}h</button>
            ))}
            <span style={{ flex: 1 }} />
            <span>{d.later_n}/{ws.length}</span>
          </div>
          {!ws.length && <div style={{ color: '#7d828c', padding: '6px 0' }}>No attributed trades in this bar.</div>}
          {ws.map(w => {
            const gc = w.group != null ? GROUP_COLORS[w.group % GROUP_COLORS.length] : null
            const L = w.later
            return (
              <div key={w.addr} style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: gc || 'transparent', border: gc ? 'none' : '1px solid #3a3d44' }} />
                  <span title={`${w.addr}\nclick to open wallet`} style={{ cursor: 'pointer', color: '#e6e9ef' }}
                    onClick={() => { openWalletMap(w.addr); onClose() }}>{short(w.addr)}</span>
                  <span style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: pct(w.share), background: sideCol }} />
                  </span>
                  <b style={{ width: 34, textAlign: 'right' }}>{pct(w.share)}</b>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 6px', marginTop: 2, color: '#7d828c', paddingLeft: 13 }}>
                  <span>{w.sol} SOL · {w.n}×{w.avg}</span>
                  {w.fresh && <span style={tag('#00d4c8')} title={`first seen ${w.age_h}h before this bar`}>fresh {w.age_h != null ? `${Math.max(0, w.age_h)}h` : ''}</span>}
                  {w.connected?.length > 0 && <span style={tag(gc || '#ffb84a')} title={w.connected.join(', ')}>connected</span>}
                  {w.sizes_match && <span style={tag('#8b8f98')} title="repeat same-size orders">same sizes</span>}
                  {w.connected?.length > 0 && <span>{w.connected.join(' · ')}</span>}
                </div>
                <div style={{ paddingLeft: 13, marginTop: 1, color: L ? '#ffb84a' : '#555a63' }}>
                  {L ? `later: back +${fmtMin(L.after_min)} (${ctFmt.format(L.first)} CT) · ${L.buys}b/${L.sells}s · ${L.buy_sol}/${L.sell_sol} SOL`
                    : `later: not back within ${d.after_h}h`}
                </div>
              </div>
            )
          })}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button style={{ ...btn, ...btnOn }} disabled={!ws.length} onClick={openTimeline}
              title="Open the wallet panel's Timeline with these wallets highlighted">Timeline</button>
            <span style={{ flex: 1 }} />
            <span style={{ color: '#555a63' }}>{d.cached ? 'cached' : `${d.ms}ms`}</span>
          </div>
        </>
      )}
    </div>,
    document.body,
  )
}

const btn = { background: 'transparent', border: '1px solid #2c3038', color: '#c8cfdc', borderRadius: 4, padding: '1px 6px',
  font: 'inherit', cursor: 'pointer' }
const btnOn = { borderColor: '#4a9eff', color: '#4a9eff' }
const tag = (c) => ({ border: `1px solid ${c}`, color: c, borderRadius: 3, padding: '0 4px', lineHeight: '14px' })
